import i18n from 'i18next';
import {openConfirmDialog, showNotification} from 'platform/components';

import type {BulkOperationsServices} from '../types/BulkOperationsServices';
import {normalizeBulkOperationError} from '../utils/normalizeBulkOperationError';
import {usePendingBulkOperationRequests} from './usePendingBulkOperationRequests';

/** The run settled before the cancel arrived, so the shown snapshot is stale. */
const CONFLICT_STATUS = 409;

const handleApiError = () =>
  showNotification.error(i18n.t('general.notifications.defaultErrorMessage'));

/**
 * Owns cancels for the tracking lifetime, independently of panel and dialog visibility. A cancel
 * cannot be undone, so it is confirmed first. Once the server accepted it, or answered that the
 * run had already finished, `onSettled` reloads the run.
 */
export function useBulkOperationCancellation(args: {
  services: Pick<BulkOperationsServices, 'useCancel' | 'handleApiError'>;
  onSettled: (bulkOperationId: string) => void;
}) {
  const [submit] = args.services.useCancel();
  const requests = usePendingBulkOperationRequests({
    request: (operation: {bulkOperationId: string}) => submit(operation).unwrap(),
    onSuccess: (operation) => args.onSettled(operation.bulkOperationId),
    onError: (operation, error) => {
      const apiError = normalizeBulkOperationError(error);
      (args.services.handleApiError ?? handleApiError)(apiError);
      if ('status' in apiError && apiError.status === CONFLICT_STATUS) {
        args.onSettled(operation.bulkOperationId);
      }
    },
  });

  const cancel = (bulkOperationId: string) => {
    if (!requests.canStart(bulkOperationId)) {
      return;
    }
    openConfirmDialog({
      id: `cancelBulkOperation-${bulkOperationId}`,
      text: i18n.t('page.datagrid.bulkOperations.cancel.confirmation', {
        defaultValue:
          'Cancel this bulk operation? Records that have not started will be skipped, records already in progress will still finish. This cannot be undone.',
      }),
      onConfirm: () => requests.start({bulkOperationId}),
    });
  };

  return {pendingIds: requests.pendingIds, cancel};
}
